import React from 'react';
import { useTranslation } from 'react-i18next';

export type ChatMode = 'text' | 'voice';

interface Props {
    mode: ChatMode;
    onChange: (mode: ChatMode) => void;
}

/**
 * ChatModeToggle — header button that toggles between text input and
 * push-to-talk voice. Two states; button shows the *target* mode
 * (mic icon when in text mode, keyboard icon when in voice mode).
 */
const ChatModeToggle: React.FC<Props> = ({ mode, onChange }) => {
    const { t } = useTranslation('messaging');

    const next: ChatMode = mode === 'text' ? 'voice' : 'text';
    const label =
        next === 'voice'
            ? t('voice.switchToVoice', 'Switch to voice')
            : t('voice.switchToText', 'Switch to text');

    return (
        <button
            type="button"
            onClick={() => onChange(next)}
            className="
                flex items-center justify-center rounded-md w-10 h-8 text-gray-600
                transition-colors hover:bg-gray-100
                focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300
            "
            aria-label={label}
            title={label}
            data-chat-mode={mode}
        >
            {next === 'voice' ? (
                // Microphone icon
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z" />
                    <path d="M19 11a1 1 0 0 0-2 0 5 5 0 0 1-10 0 1 1 0 0 0-2 0 7 7 0 0 0 6 6.93V21a1 1 0 0 0 2 0v-3.07A7 7 0 0 0 19 11z" />
                </svg>
            ) : (
                // Keyboard icon
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                    <path d="M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6zm2 0v12h14V6H5zm2 2h2v2H7V8zm0 4h2v2H7v-2zm4-4h2v2h-2V8zm0 4h2v2h-2v-2zm4-4h2v2h-2V8zm0 4h2v2h-2v-2zm-8 4h10v2H7v-2z" />
                </svg>
            )}
        </button>
    );
};

export default ChatModeToggle;
